import React, {useState} from 'react';
import {Grid, Typography} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {Quote} from '../../../Core/mobx/ParameterStore/Quote';
import Card from '@material-ui/core/Card';
import {NumberFormatCustom, SwitchCustom, TextBox} from '../../../Core/UI';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {FormType} from '../../PreQualification/types';
import {Prequal} from '../../../Core/mobx/ParameterStore/Prequal';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/QuoteXref';

type GULTabProps = {
    valueChanged: (p: Parameter, value: any, formType?: FormType) => void;
    parameters: Quote;
    refParameters: Prequal;
    formType: FormType;
};

const GULTab = observer(({valueChanged, parameters, formType}: GULTabProps) => {
    const [error, setError] = useState({smokerRatingNA: '', specifiedFaceAmount: ''});

    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    const validateSmokerRatingNA = () => {
        const {healthRatingNA, smokerRatingNA} = parameters;

        if (['Preferred', 'Standard'].includes(healthRatingNA.value)) {
            setError((prevState) => ({
                ...prevState,
                smokerRatingNA: smokerRatingNA.value === 'Tobacco' ? '' : 'smokerRatingNA == Tobacco',
            }));
        }
    };

    const validateSpecifiedFaceAmount = () => {
        setError((prevState) => ({
            ...prevState,
            specifiedFaceAmount: +parameters.specifiedFaceAmount.value < 50000 ? 'specifiedFaceAmount >= 50000' : '',
        }));
    };

    return (
        <>
            <Card
                style={{
                    padding: 20,
                    marginBottom: 20,
                }}
            >
                <div>
                    <Typography style={{fontWeight: 'bold', fontSize: 24}}>Carrier Product</Typography>
                </div>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <SelectInputElement
                            param={{
                                label: 'Product',
                                name: 'productGUL',
                                options: fieldOptions.productGUL.options,
                                optionValues: fieldOptions.productGUL.optionValues,
                                ...parameters.productGUL,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                </Grid>
            </Card>
            <Card
                style={{
                    padding: 20,
                    marginBottom: 20,
                }}
            >
                <div>
                    <Typography style={{fontWeight: 'bold', fontSize: 24}}>Basics</Typography>
                </div>
                <Grid container spacing={3}>
                    {parameters.northAmerican.value && (
                        <>
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Health Rating',
                                        name: 'healthRatingNA',
                                        options: fieldOptions.healthRatingNA.options,
                                        ...parameters.healthRatingNA,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Smoker Status',
                                        name: 'smokerRatingNA',
                                        options: fieldOptions.smokerRatingNA.options,
                                        ...parameters.smokerRatingNA,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                    error={!!error.smokerRatingNA}
                                    helperText={error.smokerRatingNA}
                                    onBlur={validateSmokerRatingNA}
                                />
                            </Grid>
                        </>
                    )}
                    {parameters.northAmerican.value && parameters.healthRatingNA.value === 'Standard' && (
                        <Grid item xs={6}>
                            <SelectInputElement
                                param={{
                                    label: 'Table Rating',
                                    name: 'tableRatingNA',
                                    options: fieldOptions.tableRatingNA.options,
                                    optionValues: fieldOptions.tableRatingNA.optionValues,
                                    ...parameters.tableRatingNA,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                                formType={formType}
                            />
                        </Grid>
                    )}
                    <Grid item xs={6}>
                        <SwitchCustom
                            param={{
                                name: 'flatExtra',
                                label: 'Flat Extra',
                                ...parameters.flatExtra,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                    {parameters.flatExtra.value && (
                        <Grid item xs={12}>
                            <TextBox
                                id={'flatExtraPerm'}
                                label={'Policy Lifetime Flat Extra (per $1000)'}
                                value={parameters.flatExtraPerm?.value}
                                onChange={(value) => valueChanged(parameters.flatExtraPerm, value, formType)}
                                InputProps={{
                                    inputComponent: NumberFormatCustom,
                                }}
                            />
                        </Grid>
                    )}
                    {parameters.flatExtra.value && (parameters.ameritas.value || parameters.northAmerican.value) && (
                        <>
                            <Grid item xs={6}>
                                <TextBox
                                    id={'flatExtraYears'}
                                    label={'Temporary Flat Extra Years'}
                                    value={parameters.flatExtraYears?.value}
                                    onChange={(value) => valueChanged(parameters.flatExtraYears, value, formType)}
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextBox
                                    id={'flatExtraTemp'}
                                    label={'Temporary Flat Extra Amount (per $1000)'}
                                    value={parameters.flatExtraTemp?.value}
                                    onChange={(value) => valueChanged(parameters.flatExtraTemp, value, formType)}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                    }}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </Card>
            <Card
                style={{
                    padding: 20,
                    marginBottom: 20,
                }}
            >
                <div>
                    <Typography style={{fontWeight: 'bold', fontSize: 24}}>Coverage and Premium</Typography>
                </div>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <TextBox
                            id={'specifiedFaceAmount'}
                            label={required('Specified Face Amount')}
                            value={parameters.specifiedFaceAmount?.value}
                            onChange={(value) => {
                                valueChanged(parameters.specifiedFaceAmount, value, formType);
                                validateSpecifiedFaceAmount();
                            }}
                            InputProps={{
                                inputComponent: NumberFormatCustom,
                            }}
                            error={!!error.specifiedFaceAmount}
                            helperText={error.specifiedFaceAmount}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SelectInputElement
                            param={{
                                label: 'Premium Payment Frequency',
                                name: 'premiumFrequency',
                                options: fieldOptions.premiumFrequency.options,
                                optionValues: fieldOptions.premiumFrequency.optionValues,
                                ...parameters.premiumFrequency,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                </Grid>
            </Card>
            <Card
                style={{
                    padding: 20,
                    marginBottom: 20,
                }}
            >
                <div>
                    <Typography style={{fontWeight: 'bold', fontSize: 24}}>Coverage and Premium Details</Typography>
                </div>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <SelectInputElement
                            param={{
                                label: 'Solve For',
                                name: 'solveForGUL',
                                options: fieldOptions.solveForGUL.options,
                                ...parameters.solveForGUL,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                        <small>This is preset with solveScenario. You don't need to change this most likely</small>
                    </Grid>
                    <Grid item xs={6}>
                        <SelectInputElement
                            param={{
                                label: 'Death Benefit Guarantee To Age',
                                name: 'deathBenefitGuaranteeAge',
                                options: fieldOptions.deathBenefitGuaranteeAge.options,
                                ...parameters.deathBenefitGuaranteeAge,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                        />
                    </Grid>
                </Grid>
            </Card>
        </>
    );
});
export default GULTab;
